import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useTranslation } from "@/i18n/translations";
import { useNavigate } from "react-router-dom";
import { Users, Building2, Calendar, DollarSign, ExternalLink, Clock, TrendingUp, Gift, MessageSquare, Monitor } from "lucide-react";
import Footer from "@/components/Footer";
import AdminSupportTickets from "@/components/AdminSupportTickets";
import AdminApiKeys from "@/components/AdminApiKeys";
import AdminBillingAudit from "@/components/AdminBillingAudit";
import AdminDemoWidget from "@/components/AdminDemoWidget";
import AdminBlog from "@/components/AdminBlog";
import AdminExperiments from "@/components/AdminExperiments";
import AdminTraction from "@/components/AdminTraction";
import AdminBroadcasts from "@/components/AdminBroadcasts";
import AdminGreenInstances from "@/components/AdminGreenInstances";

export default function AdminDashboard({ setIsAuthenticated }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [salons, setSalons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminPeriod, setAdminPeriod] = useState(30); // Filter period for admin stats
  const [activeTab, setActiveTab] = useState('overview');
  const [billingSalon, setBillingSalon] = useState(null);
  
  // Promocode states
  const [promocodes, setPromocodes] = useState([]);
  const [newPromocode, setNewPromocode] = useState({
    code: '',
    promo_type: 'discount',
    discount_percent: 10,
    free_months: 1,
    free_plan: 'professional',
    max_uses: -1,
    valid_until: '',
    plan_scope: 'all',
    applicable_plans: ['all']
  });
  const [promoRedemptions, setPromoRedemptions] = useState({});
  const [expandedPromo, setExpandedPromo] = useState(null);
  
  // Payments states
  const [checkingPayments, setCheckingPayments] = useState(false);
  const [paymentCheckResult, setPaymentCheckResult] = useState(null);

  // Conversations analytics states
  const [convRows, setConvRows] = useState([]);
  const [convLoading, setConvLoading] = useState(false);
  const [convPeriod, setConvPeriod] = useState(30);
  const [convSort, setConvSort] = useState('total'); // total | converted | rate | lost
  const [backfilling, setBackfilling] = useState(false);
  const [renewalsBackfilling, setRenewalsBackfilling] = useState(false);
  const [greenCleaning, setGreenCleaning] = useState(false);
  const [dupes, setDupes] = useState(null);
  const [dupesLoading, setDupesLoading] = useState(false);

  useEffect(() => {
    fetchAdminData(adminPeriod);
    fetchPromocodes();
  }, []);

  const fetchAdminData = async (days = 30) => {
    try {
      const [statsRes, salonsRes] = await Promise.all([
        axios.get(`${API}/admin/stats?days=${days}`),
        axios.get(`${API}/admin/salons`)
      ]);
      
      setStats(statsRes.data);
      setSalons(salonsRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (days) => {
    setAdminPeriod(days);
    fetchAdminData(days);
  };

  // Fetch per-salon conversation analytics
  const fetchConversations = async (days = convPeriod) => {
    setConvLoading(true);
    try {
      const res = await axios.get(`${API}/admin/conversations/by-salon?days=${days}&limit=200`);
      setConvRows(res.data.rows || []);
    } catch (e) {
      toast.error('Не удалось загрузить аналитику диалогов');
    } finally {
      setConvLoading(false);
    }
  };

  const handleBackfillConversations = async () => {
    if (!window.confirm('Перестроить коллекцию conversations из chat_messages и bookings? Безопасно — использует upsert.')) return;
    setBackfilling(true);
    try {
      const res = await axios.post(`${API}/admin/conversations/backfill`);
      toast.success(`Готово! +${res.data.inserted} новых, ${res.data.marked_converted} помечены как конверсия`);
      await fetchConversations(convPeriod);
      await fetchAdminData(adminPeriod);
    } catch (e) {
      toast.error('Backfill не удался: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBackfilling(false);
    }
  };

  const handleCheckDuplicates = async () => {
    setDupesLoading(true);
    try {
      const r = await axios.get(`${API}/admin/integrity/duplicates`);
      setDupes(r.data);
      if (!r.data.total_groups) toast.success('Дублей нет: у каждого салона уникальные Stripe/Tinkoff/Telegram привязки');
      else toast.warning(`Найдено групп дублей: ${r.data.total_groups} — см. список ниже`);
    } catch (e) {
      toast.error('Проверка не удалась: ' + (e.response?.data?.detail || e.message));
    } finally {
      setDupesLoading(false);
    }
  };

  const handleGreenCleanup = async () => {
    setGreenCleaning(true);
    try {
      const dry = await axios.post(`${API}/admin/greenapi/cleanup?dry_run=true`);
      const del = dry.data.deleted || [];
      const warned = dry.data.warned || [];
      if (!del.length && !warned.length) {
        toast.success(`WhatsApp-инстансы в порядке: проверено ${dry.data.checked}, кандидатов на отключение нет`);
        return;
      }
      const list = del.map((d) => `• ${d.id_instance} — ${d.salon || '—'} (${d.email || '—'}) [${d.reason}, state=${d.state}]`).join('\n');
      const warnList = warned.map((d) => `• ${d.id_instance} — ${d.salon || '—'} (предупреждение)`).join('\n');
      if (!window.confirm(`Отключить ${del.length} WhatsApp-инстанс(ов)?\n\n${list}${warnList ? `\n\nПисьмо-предупреждение получат:\n${warnList}` : ''}`)) return;
      const res = await axios.post(`${API}/admin/greenapi/cleanup?dry_run=false`);
      toast.success(`Отключено: ${res.data.deleted.length}, предупреждено: ${res.data.warned.length}${res.data.errors?.length ? `, ошибок: ${res.data.errors.length}` : ''}`);
    } catch (e) {
      toast.error('Очистка не удалась: ' + (e.response?.data?.detail || e.message));
    } finally {
      setGreenCleaning(false);
    }
  };

  const [zernioCleaning, setZernioCleaning] = useState(false);
  const handleZernioCleanup = async () => {
    setZernioCleaning(true);
    try {
      const dry = await axios.post(`${API}/admin/zernio/cleanup?dry_run=true`);
      const { deleted = [], warned = [], orphans = [], checked = 0 } = dry.data;
      const fmt = (d) => `• @${d.username || '—'} — ${d.salon || '—'} (${d.email || '—'}) [${d.reason}${d.unpaid_since ? `, не оплачено с ${d.unpaid_since}` : ''}]`;
      const orphanTxt = orphans.length ? `\n\nАккаунты в Zernio без привязки в Chatity (платим зря):\n${orphans.map((o) => `• @${o.username} (${o.platform}, ${o.profile || '—'}, с ${o.created})`).join('\n')}` : '';
      if (!deleted.length && !warned.length) {
        window.alert(`Instagram (Zernio) в порядке: проверено ${checked}, кандидатов на отключение нет.${orphanTxt}`);
        return;
      }
      if (!window.confirm(`Отключить ${deleted.length} Instagram-аккаунт(ов) (подписка не активна >7 дней)?\n\n${deleted.map(fmt).join('\n')}${warned.length ? `\n\nПисьмо-предупреждение получат:\n${warned.map(fmt).join('\n')}` : ''}${orphanTxt}`)) return;
      const res = await axios.post(`${API}/admin/zernio/cleanup?dry_run=false`);
      toast.success(`Instagram отключено: ${res.data.deleted.length}, предупреждено: ${res.data.warned.length}${res.data.errors?.length ? `, ошибок: ${res.data.errors.length}` : ''}`);
    } catch (e) {
      toast.error('Проверка Zernio не удалась: ' + (e.response?.data?.detail || e.message));
    } finally {
      setZernioCleaning(false);
    }
  };

  const handleBackfillRenewals = async () => {
    if (!window.confirm('Синхронизировать историю платежей со Stripe для ВСЕХ пользователей? Подтянет все оплаченные счета (продления и выставленные вручную). Безопасно — дубликаты не создаются.')) return;
    setRenewalsBackfilling(true);
    try {
      const res = await axios.post(`${API}/admin/payments/backfill-renewals`);
      toast.success(`Готово! Проверено счетов: ${res.data.invoices_checked}, добавлено платежей: ${res.data.inserted} (салонов с оплатами: ${res.data.salons_with_invoices || 0} из ${res.data.salons_scanned})`);
      if (res.data.reconciled?.length) toast.info(`Восстановлено подписок по данным Stripe: ${res.data.reconciled.length} (снят флаг «оплата не прошла» / включён кабинет)`);
      if (res.data.errors?.length) toast.warning(`Ошибок: ${res.data.errors.length} — см. логи`);
      await fetchAdminData(adminPeriod);
    } catch (e) {
      toast.error('Синхронизация не удалась: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRenewalsBackfilling(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'conversations') {
      fetchConversations(convPeriod);
    }
  }, [activeTab, convPeriod]);

  const sortedConvRows = [...convRows].sort((a, b) => {
    const k = convSort === 'rate' ? 'conversion_rate'
      : convSort === 'converted' ? 'converted_conversations'
      : convSort === 'lost' ? 'lost_conversations'
      : 'total_conversations';
    return (b[k] || 0) - (a[k] || 0);
  });

  // Users table: filter + newest-first sorting
  const [userFilter, setUserFilter] = useState('all');
  const classifyUser = (s) => s.is_paid ? 'paid' : (s.trial_days_left > 0 ? 'trial' : 'expired');
  const userCounts = {
    all: salons.length,
    paid: salons.filter((s) => classifyUser(s) === 'paid').length,
    trial: salons.filter((s) => classifyUser(s) === 'trial').length,
    expired: salons.filter((s) => classifyUser(s) === 'expired').length,
  };
  const visibleSalons = salons
    .filter((s) => userFilter === 'all' || classifyUser(s) === userFilter)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const pct = (n) => salons.length ? Math.round((n / salons.length) * 100) : 0;


  // Promocode functions
  const fetchPromocodes = async () => {
    try {
      const response = await axios.get(`${API}/admin/promocodes`);
      setPromocodes(response.data.promocodes);
    } catch (error) {
      console.error('Error fetching promocodes:', error);
    }
  };

  const createPromocode = async () => {
    try {
      if (!newPromocode.code || newPromocode.code.length < 3) {
        toast.error('Промокод должен быть минимум 3 символа');
        return;
      }
      
      const planScopeMap = {
        all: ['all'],
        yearly: ['starter_yearly', 'professional_yearly', 'unlimited_yearly'],
        monthly: ['starter', 'professional', 'unlimited']
      };
      await axios.post(`${API}/admin/promocodes`, {
        ...newPromocode,
        applicable_plans: planScopeMap[newPromocode.plan_scope] || ['all']
      });
      toast.success('Промокод создан успешно!');
      
      // Reset form
      setNewPromocode({
        code: '',
        promo_type: 'discount',
        discount_percent: 10,
        free_months: 1,
        free_plan: 'professional',
        max_uses: -1,
        valid_until: '',
        plan_scope: 'all',
        applicable_plans: ['all']
      });
      
      // Refresh list
      fetchPromocodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка создания промокода');
    }
  };

  const togglePromocode = async (code) => {
    try {
      await axios.put(`${API}/admin/promocodes/${code}/toggle`);
      toast.success('Статус промокода обновлён');
      fetchPromocodes();
    } catch (error) {
      toast.error('Ошибка обновления промокода');
    }
  };

  const deletePromocode = async (code) => {
    if (!window.confirm(`Удалить промокод ${code}?`)) return;
    
    try {
      await axios.delete(`${API}/admin/promocodes/${code}`);
      toast.success('Промокод удалён');
      fetchPromocodes();
    } catch (error) {
      toast.error('Ошибка удаления промокода');
    }
  };

  const toggleRedemptions = async (code) => {
    if (expandedPromo === code) { setExpandedPromo(null); return; }
    try {
      const res = await axios.get(`${API}/admin/promocodes/${code}/redemptions`);
      setPromoRedemptions(prev => ({ ...prev, [code]: res.data.redemptions }));
      setExpandedPromo(code);
    } catch (error) {
      toast.error('Ошибка загрузки активаций');
    }
  };

  const copyPromoLink = (code) => {
    navigator.clipboard.writeText(`https://chatity.io/auth?promo=${code}`);
    toast.success('Ссылка скопирована: chatity.io/auth?promo=' + code);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setIsAuthenticated(false);
    toast.success(t('dashboard.logoutSuccess'));
    navigate('/auth');
  };

  const handleGrantUnlimited = async (userId) => {
    if (!window.confirm(t('admin.confirmGrant'))) {
      return;
    }

    try {
      await axios.post(`${API}/admin/users/${userId}/grant-unlimited`);
      toast.success(t('admin.unlimitedGranted'));
      // Refresh data
      fetchAdminData(adminPeriod);
    } catch (error) {
      toast.error(t('admin.error'));
      console.error('Error granting unlimited:', error);
    }
  };

  const handleSetPassword = async (userEmail) => {
    const newPassword = window.prompt(`Новый пароль для ${userEmail} (минимум 8 символов):`);
    if (!newPassword) return;
    if (newPassword.length < 8) {
      toast.error('Пароль должен быть минимум 8 символов');
      return;
    }
    try {
      await axios.post(`${API}/admin/users/set-password`, { email: userEmail, new_password: newPassword });
      toast.success(`Пароль для ${userEmail} обновлён`);
    } catch (error) {
      toast.error('Ошибка: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`${t('admin.confirmDelete')}\n\nEmail: ${userEmail}`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success(t('admin.accountDeleted'));
      // Refresh data
      fetchAdminData(adminPeriod);
    } catch (error) {
      toast.error(t('admin.error'));
      console.error('Error deleting user:', error);
    }
  };


  const handleCheckPendingPayments = async () => {
    setCheckingPayments(true);
    setPaymentCheckResult(null);
    
    try {
      toast.info('Проверка pending платежей...', { duration: 2000 });
      
      const response = await axios.post(`${API}/payments/check-pending`);
      
      setPaymentCheckResult(response.data);
      
      if (response.data.updated > 0) {
        toast.success(
          `✅ Обновлено: ${response.data.updated} из ${response.data.checked} платежей`,
          { duration: 5000 }
        );
        // Refresh admin data
        fetchAdminData(adminPeriod);
      } else {
        toast.info(
          `Проверено: ${response.data.checked} платежей. Все актуальны.`,
          { duration: 3000 }
        );
      }
      
      if (response.data.errors.length > 0) {
        console.warn('Payment check errors:', response.data.errors);
      }
      
    } catch (error) {
      toast.error('Ошибка проверки платежей: ' + (error.response?.data?.detail || error.message));
      console.error('Error checking payments:', error);
    } finally {
      setCheckingPayments(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl">{t('admin.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Mobile optimized */}
      <header className="bg-white border-b-2 border-black sticky top-0 z-50">
        <div className="container mx-auto px-3 md:px-4 py-3 md:py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Building2 className="w-6 h-6 md:w-8 md:h-8" />
            <h1 className="text-base md:text-2xl font-bold">Панель Админа</h1>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            size="sm"
            className="border-2 border-black bg-white text-black hover:bg-black hover:text-white font-medium text-xs md:text-sm"
          >
            Выход
          </Button>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="container mx-auto px-3 md:px-4 py-4 border-b-2 border-black bg-white">
        <div className="flex gap-2 justify-center flex-wrap">
          <Button
            onClick={() => setActiveTab('overview')}
            className={`${activeTab === 'overview' ? 'bg-black text-white' : 'bg-white text-black border-2 border-black'} font-semibold`}
            data-testid="tab-overview"
          >
            📊 Обзор
          </Button>
          <Button
            onClick={() => setActiveTab('conversations')}
            className={`${activeTab === 'conversations' ? 'bg-black text-white' : 'bg-white text-black border-2 border-black'} font-semibold`}
            data-testid="tab-conversations"
          >
            💬 Диалоги
          </Button>
          <Button
            onClick={() => setActiveTab('traction')}
            className={`${activeTab === 'traction' ? 'bg-black text-white' : 'bg-white text-black border-2 border-black'} font-semibold`}
            data-testid="tab-traction"
          >
            📈 Traction
          </Button>
          <Button
            onClick={() => setActiveTab('experiments')}
            className={`${activeTab === 'experiments' ? 'bg-black text-white' : 'bg-white text-black border-2 border-black'} font-semibold`}
            data-testid="tab-experiments"
          >
            🧪 A/B
          </Button>
          <Button
            onClick={() => setActiveTab('promocodes')}
            className={`${activeTab === 'promocodes' ? 'bg-black text-white' : 'bg-white text-black border-2 border-black'} font-semibold`}
          >
            🎫 Промокоды
          </Button>
          <Button
            onClick={() => setActiveTab('payments')}
            className={`${activeTab === 'payments' ? 'bg-black text-white' : 'bg-white text-black border-2 border-black'} font-semibold`}
          >
            💳 Платежи
          </Button>
          <Button
            data-testid="admin-tab-api-keys"
            onClick={() => setActiveTab('api-keys')}
            className={`${activeTab === 'api-keys' ? 'bg-black text-white' : 'bg-white text-black border-2 border-black'} font-semibold`}
          >
            🔑 API-ключи
          </Button>
          <Button
            onClick={() => setActiveTab('support')}
            className={`${activeTab === 'support' ? 'bg-black text-white' : 'bg-white text-black border-2 border-black'} font-semibold`}
          >
            🎫 Тикеты
          </Button>
          <Button
            onClick={() => setActiveTab('demo-widget')}
            className={`${activeTab === 'demo-widget' ? 'bg-black text-white' : 'bg-white text-black border-2 border-black'} font-semibold`}
          >
            🖥️ Demo Виджет
          </Button>
          <Button
            onClick={() => setActiveTab('blog')}
            className={`${activeTab === 'blog' ? 'bg-black text-white' : 'bg-white text-black border-2 border-black'} font-semibold`}
          >
            📝 Блог
          </Button>
          <Button
            onClick={() => setActiveTab('broadcasts')}
            className={`${activeTab === 'broadcasts' ? 'bg-black text-white' : 'bg-white text-black border-2 border-black'} font-semibold`}
            data-testid="tab-broadcasts"
          >
            📣 Рассылка
          </Button>
          <Button
            onClick={() => setActiveTab('green')}
            className={`${activeTab === 'green' ? 'bg-black text-white' : 'bg-white text-black border-2 border-black'} font-semibold`}
            data-testid="tab-green"
          >
            📲 Инстансы WA
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {activeTab === 'traction' && (
        <div className="max-w-[1200px] mx-auto px-4 pb-10"><AdminTraction /></div>
      )}
      {activeTab === 'overview' && (
      <div className="container mx-auto px-3 md:px-4 py-4 md:py-8">
        {/* Period Filter - Mobile optimized */}
        <div className="flex gap-1 md:gap-2 justify-center mb-4 md:mb-6 overflow-x-auto pb-2">
          <Button
            onClick={() => handlePeriodChange(7)}
            variant={adminPeriod === 7 ? "default" : "outline"}
            size="sm"
            className={`${adminPeriod === 7 ? "bg-black text-white hover:bg-gray-800" : "border-2 border-black bg-white text-black hover:bg-gray-100 font-medium"} text-xs md:text-sm whitespace-nowrap`}
          >
            7 дней
          </Button>
          <Button
            onClick={() => handlePeriodChange(30)}
            variant={adminPeriod === 30 ? "default" : "outline"}
            size="sm"
            className={`${adminPeriod === 30 ? "bg-black text-white hover:bg-gray-800" : "border-2 border-black bg-white text-black hover:bg-gray-100 font-medium"} text-xs md:text-sm whitespace-nowrap`}
          >
            30 дней
          </Button>
          <Button
            onClick={() => handlePeriodChange(90)}
            variant={adminPeriod === 90 ? "default" : "outline"}
            size="sm"
            className={`${adminPeriod === 90 ? "bg-black text-white hover:bg-gray-800" : "border-2 border-black bg-white text-black hover:bg-gray-100 font-medium"} text-xs md:text-sm whitespace-nowrap`}
          >
            90 дней
          </Button>
          <Button
            onClick={() => handlePeriodChange(0)}
            variant={adminPeriod === 0 ? "default" : "outline"}
            size="sm"
            className={`${adminPeriod === 0 ? "bg-black text-white hover:bg-gray-800" : "border-2 border-black bg-white text-black hover:bg-gray-100 font-medium"} text-xs md:text-sm whitespace-nowrap`}
          >
            Всё время
          </Button>
          <Button
            onClick={handleBackfillRenewals}
            size="sm"
            disabled={renewalsBackfilling}
            className="bg-yellow-100 text-black border-2 border-black hover:bg-yellow-200 text-xs md:text-sm whitespace-nowrap font-medium"
            data-testid="payments-backfill-btn"
          >
            {renewalsBackfilling ? '⏳ Синхронизация…' : '💳 Синхр. платежей Stripe'}
          </Button>
          <Button
            onClick={handleGreenCleanup}
            size="sm"
            disabled={greenCleaning}
            className="bg-green-100 text-black border-2 border-black hover:bg-green-200 text-xs md:text-sm whitespace-nowrap font-medium"
            data-testid="green-cleanup-btn"
          >
            {greenCleaning ? '⏳ Проверяем…' : '🧹 Очистить WA-инстансы'}
          </Button>
          <Button
            onClick={handleZernioCleanup}
            size="sm"
            disabled={zernioCleaning}
            className="bg-pink-100 text-black border-2 border-black hover:bg-pink-200 text-xs md:text-sm whitespace-nowrap font-medium"
            data-testid="zernio-cleanup-btn"
          >
            {zernioCleaning ? '⏳ Проверяем…' : '📸 Проверить Instagram (Zernio)'}
          </Button>
          <Button
            onClick={handleCheckDuplicates}
            size="sm"
            disabled={dupesLoading}
            className="bg-red-50 text-black border-2 border-black hover:bg-red-100 text-xs md:text-sm whitespace-nowrap font-medium"
            data-testid="integrity-duplicates-btn"
          >
            {dupesLoading ? '⏳ Проверяем…' : '🔍 Дубли привязок'}
          </Button>
        </div>

        {dupes && dupes.total_groups > 0 && (
          <div className="mb-4 border-2 border-red-400 bg-red-50 rounded-xl p-4 text-sm" data-testid="integrity-duplicates-panel">
            <div className="font-bold mb-2">🚨 Салоны с одинаковыми привязками (риск чужих уведомлений)</div>
            {['stripe_customer_id', 'stripe_subscription_id', 'tinkoff_rebill_id', 'telegram_chat_id'].map((f) => (dupes[f] || []).length > 0 && (
              <div key={f} className="mb-2">
                <div className="font-semibold text-xs uppercase text-red-700">{f}</div>
                {dupes[f].map((g) => (
                  <div key={g.value} className="ml-2 text-xs">
                    <span className="font-mono">{g.value}</span> → {g.salons.map((x) => `${x.salon_name || x.id} (${x.email || '—'}${x.is_paid ? ', paid' : ''})`).join(' · ')}
                  </div>
                ))}
              </div>
            ))}
            <button type="button" className="text-xs underline mt-1" onClick={() => setDupes(null)}>Скрыть</button>
          </div>
        )}

        {/* Main Stats Cards - Mobile optimized */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-8">
          <div className="bg-blue-50 border-2 border-black p-3 md:p-6 rounded-lg">
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3 mb-2">
              <Users className="w-5 h-5 md:w-6 md:h-6 text-black" />
              <h3 className="font-bold text-gray-800 text-xs md:text-base">Пользователей</h3>
            </div>
            <div className="text-2xl md:text-3xl font-bold text-black">{stats?.total_users || 0}</div>
          </div>

          <div className="bg-green-50 border-2 border-black p-3 md:p-6 rounded-lg">
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3 mb-2">
              <Calendar className="w-5 h-5 md:w-6 md:h-6 text-black" />
              <h3 className="font-bold text-gray-800 text-xs md:text-base">Заявок</h3>
            </div>
            <div className="text-2xl md:text-3xl font-bold text-black">{stats?.total_bookings || 0}</div>
          </div>

          <div className="bg-yellow-50 border-2 border-black p-3 md:p-6 rounded-lg">
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3 mb-2">
              <Building2 className="w-5 h-5 md:w-6 md:h-6 text-black" />
              <h3 className="font-bold text-gray-800 text-xs md:text-base">Платн / Проб</h3>
            </div>
            <div className="text-2xl md:text-3xl font-bold text-black">{stats?.paid_users || 0} / {stats?.trial_users || 0}</div>
          </div>

          <div className="bg-purple-50 border-2 border-black p-3 md:p-6 rounded-lg">
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3 mb-2">
              <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-black" />
              <h3 className="font-bold text-gray-800 text-xs md:text-base">Конверсия</h3>
            </div>
            <div className="text-3xl font-bold text-black">{stats?.conversion_rate || 0}%</div>
            <div className="text-sm text-gray-700 font-medium mt-1">пробный → платный</div>
          </div>
        </div>

        {/* Conversation & Booking Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <div className="bg-indigo-50 border-2 border-black p-4 md:p-6 rounded-lg" data-testid="card-total-conversations">
            <div className="flex items-center gap-2 md:gap-3 mb-2">
              <MessageSquare className="w-5 h-5 md:w-6 md:h-6 text-black" />
              <h3 className="font-bold text-gray-800 text-xs md:text-base">Всего диалогов</h3>
            </div>
            <div className="text-2xl md:text-3xl font-bold text-black">{stats?.total_conversations || 0}</div>
            <div className="text-xs md:text-sm text-gray-700 font-medium mt-1">уникальных сессий</div>
          </div>

          <div className="bg-green-50 border-2 border-black p-4 md:p-6 rounded-lg" data-testid="card-converted-conversations">
            <div className="flex items-center gap-2 md:gap-3 mb-2">
              <Calendar className="w-5 h-5 md:w-6 md:h-6 text-black" />
              <h3 className="font-bold text-gray-800 text-xs md:text-base">С заявкой</h3>
            </div>
            <div className="text-2xl md:text-3xl font-bold text-green-700">{stats?.converted_conversations ?? stats?.total_bookings ?? 0}</div>
            <div className="text-xs md:text-sm text-gray-700 font-medium mt-1">конверсия в бронь</div>
          </div>

          <div className="bg-red-50 border-2 border-black p-4 md:p-6 rounded-lg" data-testid="card-lost-conversations">
            <div className="flex items-center gap-2 md:gap-3 mb-2">
              <Users className="w-5 h-5 md:w-6 md:h-6 text-black" />
              <h3 className="font-bold text-gray-800 text-xs md:text-base">Без заявки</h3>
            </div>
            <div className="text-2xl md:text-3xl font-bold text-red-700">{stats?.lost_conversations ?? 0}</div>
            <div className="text-xs md:text-sm text-gray-700 font-medium mt-1">потеряно (lost leads)</div>
          </div>

          <div className="bg-orange-50 border-2 border-black p-4 md:p-6 rounded-lg" data-testid="card-conversion-rate">
            <div className="flex items-center gap-2 md:gap-3 mb-2">
              <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-black" />
              <h3 className="font-bold text-gray-800 text-xs md:text-base">Конверсия</h3>
            </div>
            <div className="text-2xl md:text-3xl font-bold text-black">{stats?.conversations_to_bookings_rate || 0}%</div>
            <div className="text-xs md:text-sm text-gray-700 font-medium mt-1">
              {stats?.total_conversations > 0
                ? `${stats?.converted_conversations ?? stats?.total_bookings ?? 0} из ${stats?.total_conversations || 0}`
                : 'Пока нет диалогов'}
            </div>
          </div>
        </div>

        {/* Revenue Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <div className="border-2 border-green-400 bg-green-50 p-4 md:p-6 rounded-lg" data-testid="revenue-month">
            <div className="text-xs text-gray-700 font-medium">💰 Выручка за месяц (факт)</div>
            <div className="text-2xl font-bold text-green-700 mt-1">€{stats?.actual_revenue_month || 0}</div>
          </div>
          <div className="border-2 border-green-400 bg-green-50 p-4 md:p-6 rounded-lg" data-testid="revenue-year">
            <div className="text-xs text-gray-700 font-medium">💰 Выручка за год (факт)</div>
            <div className="text-2xl font-bold text-black mt-1">€{stats?.actual_revenue_year || 0}</div>
          </div>
          <div className="border-2 border-blue-300 bg-blue-50 p-4 md:p-6 rounded-lg" data-testid="mrr-card">
            <div className="text-xs text-gray-700 font-medium">📈 MRR (прогноз)</div>
            <div className="text-2xl font-bold text-blue-700 mt-1">€{stats?.mrr || 0}</div>
            <div className="text-[10px] text-gray-500 mt-1">только реальные оплаты Stripe ({stats?.real_paid_users ?? 0} подписок)</div>
          </div>
          <div className="border-2 border-blue-300 bg-blue-50 p-4 md:p-6 rounded-lg" data-testid="arr-card">
            <div className="text-xs text-gray-700 font-medium">📈 ARR (прогноз)</div>
            <div className="text-2xl font-bold text-black mt-1">€{stats?.arr || 0}</div>
            <div className="text-[10px] text-gray-500 mt-1">ручные апгрейды не учитываются</div>
          </div>
        </div>

        {/* Advanced Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Plan Breakdown */}
          <div className="border-2 border-blue-300 bg-blue-50 p-6 rounded-lg">
            <h3 className="font-bold mb-3 text-black">📊 По тарифам (MRR)</h3>
            <div className="space-y-2">
              {[
                ['starter', 'Starter'],
                ['professional', 'Professional'],
                ['unlimited', 'Unlimited [legacy]'],
              ].map(([key, label]) => (
                <div key={key} className="flex justify-between items-center bg-white/60 px-3 py-2 rounded">
                  <span className="text-sm font-medium text-gray-800">
                    {label} (€{stats?.plan_breakdown?.[key]?.price || 0})
                  </span>
                  <span className="text-sm text-gray-600">
                    {stats?.plan_breakdown?.[key]?.monthly_count || 0}м / {stats?.plan_breakdown?.[key]?.yearly_count || 0}г
                  </span>
                  <span className="text-sm font-bold text-black">€{stats?.plan_breakdown?.[key]?.revenue || 0}/мес</span>
                </div>
              ))}
            </div>
          </div>

          {/* Churn & Retention */}
          <div className="border-2 border-red-300 bg-red-50 p-6 rounded-lg">
            <h3 className="font-bold mb-3 text-black">📉 Churn Rate</h3>
            <div className="space-y-3">
              <div>
                <div className="text-xs text-gray-700 font-medium">Отток клиентов (месяц)</div>
                <div className="text-2xl font-bold text-red-700">{stats?.customer_churn_rate || 0}%</div>
              </div>
            </div>
          </div>

          {/* Net Revenue Retention */}
          <div className="border-2 border-purple-300 bg-purple-50 p-6 rounded-lg">
            <h3 className="font-bold mb-3 text-black">📈 Net Revenue Retention</h3>
            <div className="space-y-3">
              <div>
                <div className="text-xs text-gray-700 font-medium">NRR (новые + апгрейды - отток)</div>
                <div className="text-2xl font-bold text-purple-700">{stats?.nrr || 0}%</div>
              </div>
              <div className="text-xs text-gray-600 mt-2">
                {stats?.nrr >= 100 ? '✅ Растем!' : stats?.nrr >= 90 ? '⚠️ Стабильно' : '🔻 Снижение'}
              </div>
            </div>
          </div>
        </div>

        {/* Period Stats */}
        {adminPeriod > 0 && stats && (
          <div className="border-2 border-blue-300 bg-blue-50 p-6 rounded-lg mb-8">
            <h3 className="font-bold mb-4 text-lg">
              Статистика за {adminPeriod} дней
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-center">
              <div className="bg-white border-2 border-black p-4 rounded-lg">
                <div className="text-3xl font-bold text-blue-600">{stats.new_users_period || 0}</div>
                <div className="text-sm text-gray-600 mt-1">Новых пользователей</div>
              </div>
              <div className="bg-white border-2 border-black p-4 rounded-lg">
                <div className="text-3xl font-bold text-purple-600">{stats.new_bookings_period || 0}</div>
                <div className="text-sm text-gray-600 mt-1">Новых заявок</div>
              </div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white border-2 border-black rounded-lg overflow-hidden">
          <div className="p-4 border-b-2 border-black">
            <h2 className="text-xl font-bold text-black">Все пользователи</h2>
            {/* Funnel by stages */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3" data-testid="users-funnel">
              <div className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2">
                <div className="text-xs text-gray-600">Регистрации</div>
                <div className="text-lg font-bold text-black">{userCounts.all} <span className="text-xs font-medium text-gray-500">100%</span></div>
              </div>
              <div className="bg-blue-50 border border-blue-300 rounded-lg px-3 py-2">
                <div className="text-xs text-gray-600">Активный триал</div>
                <div className="text-lg font-bold text-blue-700">{userCounts.trial} <span className="text-xs font-medium text-gray-500">{pct(userCounts.trial)}%</span></div>
              </div>
              <div className="bg-green-50 border border-green-300 rounded-lg px-3 py-2">
                <div className="text-xs text-gray-600">Оплатили</div>
                <div className="text-lg font-bold text-green-700">{userCounts.paid} <span className="text-xs font-medium text-gray-500">{pct(userCounts.paid)}%</span></div>
              </div>
              <div className="bg-red-50 border border-red-300 rounded-lg px-3 py-2">
                <div className="text-xs text-gray-600">Триал истёк без оплаты</div>
                <div className="text-lg font-bold text-red-700">{userCounts.expired} <span className="text-xs font-medium text-gray-500">{pct(userCounts.expired)}%</span></div>
              </div>
            </div>
            {/* Filter tabs */}
            <div className="flex flex-wrap gap-2 mt-3" data-testid="users-filter-tabs">
              {[
                ['all', `Все (${userCounts.all})`],
                ['paid', `💰 Активные (${userCounts.paid})`],
                ['trial', `🎁 Триальные (${userCounts.trial})`],
                ['expired', `⚠️ Неоплаченные (${userCounts.expired})`],
              ].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setUserFilter(key)}
                  data-testid={`users-filter-${key}`}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-full border-2 border-black transition-colors ${
                    userFilter === key ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-black text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-bold">Email</th>
                  <th className="px-4 py-3 text-left font-bold">Название</th>
                  <th className="px-4 py-3 text-left font-bold">Тариф</th>
                  <th className="px-4 py-3 text-left font-bold">Статус</th>
                  <th className="px-4 py-3 text-left font-bold">Trial</th>
                  <th className="px-4 py-3 text-left font-bold">Заявок</th>
                  <th className="px-4 py-3 text-left font-bold">Регистрация</th>
                  <th className="px-4 py-3 text-left font-bold">Сайт</th>
                  <th className="px-4 py-3 text-center font-bold">Действия</th>
                </tr>
              </thead>
              <tbody>
                {visibleSalons.map((salon, index) => (
                  <tr 
                    key={salon.id}
                    className={`border-b border-gray-200 hover:bg-blue-50 transition-colors ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}
                  >
                    <td className="px-4 py-3 text-sm text-black">{salon.user_email}</td>
                    <td className="px-4 py-3 font-medium text-black">{salon.salon_name}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-bold uppercase border border-gray-300">
                        {salon.plan || 'starter'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {salon.is_paid ? (
                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold border border-green-300">
                          💰 PAID
                        </span>
                      ) : salon.trial_days_left > 0 ? (
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-bold border border-blue-300">
                          🎁 TRIAL
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-bold border border-red-300">
                          ⚠️ EXPIRED
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {salon.is_paid ? (
                        <span className="text-gray-400 text-sm">—</span>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-orange-500" />
                          <span className={`text-sm font-medium ${
                            salon.trial_days_left <= 0 ? 'text-red-600' : 
                            salon.trial_days_left <= 2 ? 'text-orange-600' : 
                            'text-green-600'
                          }`}>
                            {salon.trial_days_left}d
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center font-bold text-black">
                      {salon.booking_count}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(salon.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      {salon.website_url ? (
                        <a 
                          href={salon.website_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 hover:underline text-sm"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Link
                        </a>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 justify-center">
                        {salon.plan !== 'unlimited' && (
                          <Button
                            onClick={() => handleGrantUnlimited(salon.user_id)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1"
                            title="Назначить безлимитный план"
                          >
                            ∞
                          </Button>
                        )}
                        <Button
                          onClick={() => handleSetPassword(salon.user_email)}
                          size="sm"
                          data-testid={`admin-set-password-${salon.user_id}`}
                          className="bg-slate-700 hover:bg-slate-800 text-white text-xs px-3 py-1"
                          title="Сменить пароль пользователя"
                        >
                          🔑
                        </Button>
                        <Button
                          data-testid={`admin-billing-audit-${salon.id}`}
                          onClick={() => setBillingSalon(salon)}
                          size="sm"
                          className="bg-white hover:bg-gray-100 text-black border-2 border-black text-xs px-3 py-1"
                          title="Аудит биллинга Stripe / остановить списания"
                        >
                          💳
                        </Button>
                        <Button
                          onClick={() => handleDeleteUser(salon.user_id, salon.user_email)}
                          size="sm"
                          className="bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1"
                          title="Удалить аккаунт"
                        >
                          🗑️
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {visibleSalons.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              Нет пользователей в этой категории
            </div>
          )}
        </div>
      </div>
      )}

      {/* Experiments Tab */}
      {activeTab === 'experiments' && (
        <div className="container mx-auto px-3 md:px-4 py-4 md:py-8">
          <AdminExperiments />
        </div>
      )}

      {/* Promocodes Tab */}
      {activeTab === 'promocodes' && (
        <div className="container mx-auto px-3 md:px-4 py-4 md:py-8">
          <Card className="bg-white text-black border-2 md:border-4 border-black">
            <CardHeader>
              <CardTitle className="text-2xl">🎫 Управление промокодами</CardTitle>
              <CardDescription>Создавайте промокоды со скидками для пользователей</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* Create Promocode Form */}
              <div className="bg-gray-50 p-6 rounded-lg border-2 border-black">
                <h3 className="text-lg font-bold mb-4">Создать новый промокод</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Код промокода</label>
                    <input
                      type="text"
                      value={newPromocode.code}
                      onChange={(e) => setNewPromocode({...newPromocode, code: e.target.value.toUpperCase()})}
                      placeholder="SUMMER2024"
                      className="w-full px-4 py-2 border-2 border-black rounded-md uppercase"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Тип промокода</label>
                    <select
                      value={newPromocode.promo_type}
                      onChange={(e) => setNewPromocode({...newPromocode, promo_type: e.target.value})}
                      className="w-full px-4 py-2 border-2 border-black rounded-md bg-white"
                      data-testid="promo-type-select"
                    >
                      <option value="discount">Скидка (%)</option>
                      <option value="free_months">Бесплатные месяцы</option>
                    </select>
                  </div>
                  
                  {newPromocode.promo_type === 'discount' ? (
                    <>
                    <div>
                      <label className="block text-sm font-medium mb-2">Скидка (%)</label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={newPromocode.discount_percent}
                        onChange={(e) => setNewPromocode({...newPromocode, discount_percent: parseInt(e.target.value)})}
                        className="w-full px-4 py-2 border-2 border-black rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Применим к тарифам</label>
                      <select
                        value={newPromocode.plan_scope}
                        onChange={(e) => setNewPromocode({...newPromocode, plan_scope: e.target.value})}
                        className="w-full px-4 py-2 border-2 border-black rounded-md bg-white"
                        data-testid="promo-plan-scope-select"
                      >
                        <option value="all">Все тарифы</option>
                        <option value="yearly">Только годовые</option>
                        <option value="monthly">Только месячные</option>
                      </select>
                    </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-2">Бесплатных месяцев</label>
                        <input
                          type="number"
                          min="1"
                          max="24"
                          value={newPromocode.free_months}
                          onChange={(e) => setNewPromocode({...newPromocode, free_months: parseInt(e.target.value)})}
                          className="w-full px-4 py-2 border-2 border-black rounded-md"
                          data-testid="promo-free-months-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Тариф в подарок</label>
                        <select
                          value={newPromocode.free_plan}
                          onChange={(e) => setNewPromocode({...newPromocode, free_plan: e.target.value})}
                          className="w-full px-4 py-2 border-2 border-black rounded-md bg-white"
                          data-testid="promo-free-plan-select"
                        >
                          <option value="starter">Starter</option>
                          <option value="professional">Professional (PRO)</option>
                          <option value="unlimited">Unlimited</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Макс. использований (-1 = безлимит)</label>
                    <input
                      type="number"
                      value={newPromocode.max_uses}
                      onChange={(e) => setNewPromocode({...newPromocode, max_uses: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 border-2 border-black rounded-md"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Действителен до (опционально)</label>
                    <input
                      type="datetime-local"
                      value={newPromocode.valid_until}
                      onChange={(e) => setNewPromocode({...newPromocode, valid_until: e.target.value})}
                      className="w-full px-4 py-2 border-2 border-black rounded-md"
                    />
                  </div>
                </div>
                
                <Button 
                  onClick={createPromocode}
                  className="mt-4 bg-black text-white hover:bg-gray-800"
                >
                  Создать промокод
                </Button>
              </div>
              
              {/* Existing Promocodes List */}
              <div>
                <h3 className="text-lg font-bold mb-4">Существующие промокоды</h3>
                <div className="space-y-3">
                  {promocodes.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">Промокодов пока нет</p>
                  ) : (
                    promocodes.map((promo) => (
                      <div key={promo.code} className="bg-gray-50 rounded-lg border-2 border-black">
                        <div className="p-4 flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-xl font-bold">{promo.code}</span>
                            {(promo.promo_type || 'discount') === 'free_months' ? (
                              <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-semibold">
                                🎁 {promo.free_months} мес. {promo.free_plan || 'professional'}
                              </span>
                            ) : (
                              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                                -{promo.discount_percent}%
                              </span>
                            )}
                            {promo.applicable_plans && !promo.applicable_plans.includes('all') && (
                              <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-semibold">
                                {promo.applicable_plans.every(p => p.endsWith('_yearly')) ? 'только годовые' : 'только месячные'}
                              </span>
                            )}
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                              promo.active ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {promo.active ? 'Активен' : 'Неактивен'}
                            </span>
                            <button
                              onClick={() => copyPromoLink(promo.code)}
                              className="text-sm text-purple-700 underline hover:text-purple-900"
                              title="Скопировать ссылку регистрации с автоприменением кода"
                              data-testid={`promo-copy-link-${promo.code}`}
                            >
                              🔗 Ссылка
                            </button>
                          </div>
                          <div className="mt-2 text-sm text-gray-600">
                            <span>Использований: {promo.current_uses} / {promo.max_uses === -1 ? '∞' : promo.max_uses}</span>
                            {promo.valid_until && (
                              <span className="ml-4">
                                До: {new Date(promo.valid_until).toLocaleDateString('ru-RU')}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap justify-end">
                          <Button
                            onClick={() => toggleRedemptions(promo.code)}
                            variant="outline"
                            className="border-2 border-black"
                            data-testid={`promo-redemptions-btn-${promo.code}`}
                          >
                            {expandedPromo === promo.code ? 'Скрыть' : 'Активации'}
                          </Button>
                          <Button
                            onClick={() => togglePromocode(promo.code)}
                            variant="outline"
                            className="border-2 border-black"
                          >
                            {promo.active ? 'Деактивировать' : 'Активировать'}
                          </Button>
                          <Button
                            onClick={() => deletePromocode(promo.code)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                          >
                            Удалить
                          </Button>
                        </div>
                        </div>
                        {expandedPromo === promo.code && (
                          <div className="border-t-2 border-black p-4 bg-white rounded-b-lg" data-testid={`promo-redemptions-list-${promo.code}`}>
                            <h4 className="font-bold mb-2 text-sm">Кто активировал ({(promoRedemptions[promo.code] || []).length}):</h4>
                            {(promoRedemptions[promo.code] || []).length === 0 ? (
                              <p className="text-gray-500 text-sm">Пока никто не активировал</p>
                            ) : (
                              <div className="space-y-1">
                                {promoRedemptions[promo.code].map((r) => (
                                  <div key={r.id} className="text-sm flex flex-wrap gap-x-3 border-b border-gray-100 py-1">
                                    <span className="font-semibold">{r.salon_name || r.salon_id}</span>
                                    <span className="text-gray-600">{r.user_email}</span>
                                    <span className="text-gray-500">
                                      {r.promo_type === 'free_months' ? `🎁 ${r.free_months} мес. ${r.plan}` : `-${r.discount_percent}%`}
                                    </span>
                                    <span className="text-gray-400 ml-auto">{new Date(r.redeemed_at).toLocaleString('ru-RU')}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payments Tab */}
      {activeTab === 'api-keys' && <AdminApiKeys />}
      {billingSalon && <AdminBillingAudit salon={billingSalon} onClose={() => setBillingSalon(null)} />}
      {activeTab === 'payments' && (
        <div className="container mx-auto px-3 md:px-4 py-4 md:py-8">
          <Card className="border-2 border-black">
            <CardHeader>
              <CardTitle className="text-xl md:text-2xl font-bold">💳 Управление Платежами</CardTitle>
              <CardDescription>
                Проверка и обновление статусов pending платежей
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Check Pending Payments Section */}
              <div className="bg-blue-50 border-2 border-black p-4 md:p-6 rounded-lg">
                <h3 className="text-lg font-bold mb-3">🔍 Проверка Pending Платежей</h3>
                <p className="text-sm text-gray-700 mb-4">
                  Эта функция проверит все pending платежи за последние 24 часа в Stripe и обновит статусы + активирует подписки.
                </p>
                
                <Button
                  onClick={handleCheckPendingPayments}
                  disabled={checkingPayments}
                  className="bg-black text-white hover:bg-gray-800 font-semibold"
                >
                  {checkingPayments ? '⏳ Проверка...' : '🔄 Проверить Pending Платежи'}
                </Button>
                
                {/* Results */}
                {paymentCheckResult && (
                  <div className="mt-4 p-4 bg-white border-2 border-black rounded-lg">
                    <h4 className="font-bold mb-2">Результаты проверки:</h4>
                    <ul className="space-y-1 text-sm">
                      <li>✓ Проверено платежей: <strong>{paymentCheckResult.checked}</strong></li>
                      <li>✓ Обновлено: <strong className="text-green-600">{paymentCheckResult.updated}</strong></li>
                      {paymentCheckResult.errors.length > 0 && (
                        <li className="text-red-600">
                          ⚠️ Ошибок: <strong>{paymentCheckResult.errors.length}</strong>
                          <ul className="ml-4 mt-1">
                            {paymentCheckResult.errors.slice(0, 5).map((err, idx) => (
                              <li key={idx} className="text-xs">{err}</li>
                            ))}
                          </ul>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              {/* Info Section */}
              <div className="bg-yellow-50 border-2 border-yellow-400 p-4 rounded-lg">
                <h4 className="font-bold mb-2">ℹ️ Информация</h4>
                <ul className="text-sm space-y-1 list-disc list-inside">
                  <li>Webhook автоматически обрабатывает платежи при получении от Stripe</li>
                  <li>Фронтенд делает 15 попыток проверки после оплаты с exponential backoff</li>
                  <li>Эта кнопка - резервный вариант для ручной проверки</li>
                  <li>Полезно если webhook не сработал или пользователь закрыл страницу</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Support Tab */}
      {activeTab === 'support' && (
        <AdminSupportTickets />
      )}

      {/* Conversations Analytics Tab */}
      {activeTab === 'conversations' && (
        <div className="container mx-auto px-3 md:px-4 py-4 md:py-8" data-testid="conversations-tab">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-bold">Аналитика диалогов по клиентам</h2>
              <p className="text-sm text-gray-700 mt-1">
                Сколько диалогов начато у каждого клиента и сколько из них конвертировалось в заявку.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => setConvPeriod(7)}
                size="sm"
                className={`${convPeriod === 7 ? 'bg-black text-white' : 'bg-white text-black border-2 border-black'} text-xs`}
                data-testid="conv-period-7"
              >7 дней</Button>
              <Button
                onClick={() => setConvPeriod(30)}
                size="sm"
                className={`${convPeriod === 30 ? 'bg-black text-white' : 'bg-white text-black border-2 border-black'} text-xs`}
                data-testid="conv-period-30"
              >30 дней</Button>
              <Button
                onClick={() => setConvPeriod(90)}
                size="sm"
                className={`${convPeriod === 90 ? 'bg-black text-white' : 'bg-white text-black border-2 border-black'} text-xs`}
                data-testid="conv-period-90"
              >90 дней</Button>
              <Button
                onClick={() => setConvPeriod(0)}
                size="sm"
                className={`${convPeriod === 0 ? 'bg-black text-white' : 'bg-white text-black border-2 border-black'} text-xs`}
                data-testid="conv-period-all"
              >Всё время</Button>
              <Button
                onClick={handleBackfillConversations}
                size="sm"
                disabled={backfilling}
                className="bg-yellow-100 text-black border-2 border-black hover:bg-yellow-200 text-xs"
                data-testid="conv-backfill-btn"
              >
                {backfilling ? '⏳ Перестройка…' : '🔄 Backfill'}
              </Button>
            </div>
          </div>

          {/* Summary cards */}
          {(() => {
            const totalT = convRows.reduce((s, r) => s + (r.total_conversations || 0), 0);
            const totalC = convRows.reduce((s, r) => s + (r.converted_conversations || 0), 0);
            const totalL = convRows.reduce((s, r) => s + (r.lost_conversations || 0), 0);
            const overallRate = totalT > 0 ? ((totalC / totalT) * 100).toFixed(2) : 0;
            return (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="bg-blue-50 border-2 border-black p-4 rounded-lg" data-testid="conv-summary-total">
                  <div className="text-xs font-bold text-gray-700">Всего диалогов</div>
                  <div className="text-2xl md:text-3xl font-bold">{totalT}</div>
                </div>
                <div className="bg-green-50 border-2 border-black p-4 rounded-lg" data-testid="conv-summary-converted">
                  <div className="text-xs font-bold text-gray-700">С заявкой</div>
                  <div className="text-2xl md:text-3xl font-bold text-green-700">{totalC}</div>
                </div>
                <div className="bg-red-50 border-2 border-black p-4 rounded-lg" data-testid="conv-summary-lost">
                  <div className="text-xs font-bold text-gray-700">Без заявки</div>
                  <div className="text-2xl md:text-3xl font-bold text-red-700">{totalL}</div>
                </div>
                <div className="bg-orange-50 border-2 border-black p-4 rounded-lg" data-testid="conv-summary-rate">
                  <div className="text-xs font-bold text-gray-700">Конверсия</div>
                  <div className="text-2xl md:text-3xl font-bold">{overallRate}%</div>
                </div>
              </div>
            );
          })()}

          {/* Per-salon table */}
          <div className="bg-white border-2 border-black rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs md:text-sm" data-testid="conv-table">
                <thead className="bg-gray-100 border-b-2 border-black">
                  <tr>
                    <th className="p-2 md:p-3 text-left font-bold">Клиент (салон)</th>
                    <th className="p-2 md:p-3 text-left font-bold hidden md:table-cell">Email</th>
                    <th className="p-2 md:p-3 text-left font-bold hidden md:table-cell">Тариф</th>
                    <th
                      className="p-2 md:p-3 text-right font-bold cursor-pointer hover:bg-gray-200"
                      onClick={() => setConvSort('total')}
                      data-testid="conv-sort-total"
                    >
                      Всего {convSort === 'total' && '▼'}
                    </th>
                    <th
                      className="p-2 md:p-3 text-right font-bold cursor-pointer hover:bg-gray-200"
                      onClick={() => setConvSort('converted')}
                      data-testid="conv-sort-converted"
                    >
                      С заявкой {convSort === 'converted' && '▼'}
                    </th>
                    <th
                      className="p-2 md:p-3 text-right font-bold cursor-pointer hover:bg-gray-200 hidden md:table-cell"
                      onClick={() => setConvSort('lost')}
                      data-testid="conv-sort-lost"
                    >
                      Без заявки {convSort === 'lost' && '▼'}
                    </th>
                    <th
                      className="p-2 md:p-3 text-right font-bold cursor-pointer hover:bg-gray-200"
                      onClick={() => setConvSort('rate')}
                      data-testid="conv-sort-rate"
                    >
                      Конверсия {convSort === 'rate' && '▼'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {convLoading ? (
                    <tr><td colSpan={7} className="p-6 text-center text-gray-600">Загрузка…</td></tr>
                  ) : sortedConvRows.length === 0 ? (
                    <tr><td colSpan={7} className="p-6 text-center text-gray-600">Нет данных. Нажмите 🔄 Backfill, чтобы построить из истории.</td></tr>
                  ) : (
                    sortedConvRows.map((r) => {
                      const rateColor = r.conversion_rate >= 30
                        ? 'text-green-700' : r.conversion_rate >= 15
                        ? 'text-orange-700' : 'text-red-700';
                      return (
                        <tr key={r.salon_id} className="border-b border-gray-200 hover:bg-gray-50" data-testid={`conv-row-${r.salon_id}`}>
                          <td className="p-2 md:p-3 font-semibold">
                            {r.salon_name}
                            {r.is_paid && <span className="ml-2 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">PAID</span>}
                          </td>
                          <td className="p-2 md:p-3 text-gray-600 hidden md:table-cell">{r.user_email || '—'}</td>
                          <td className="p-2 md:p-3 text-gray-600 hidden md:table-cell">{r.plan || '—'}</td>
                          <td className="p-2 md:p-3 text-right font-bold">{r.total_conversations}</td>
                          <td className="p-2 md:p-3 text-right font-bold text-green-700">{r.converted_conversations}</td>
                          <td className="p-2 md:p-3 text-right font-bold text-red-700 hidden md:table-cell">{r.lost_conversations}</td>
                          <td className={`p-2 md:p-3 text-right font-bold ${rateColor}`}>{r.conversion_rate.toFixed(1)}%</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {convRows.length > 0 && (
            <div className="mt-4 text-xs text-gray-600">
              Показано {convRows.length} салонов. Цвет конверсии: 🟢 ≥30% &nbsp; 🟠 15–29% &nbsp; 🔴 &lt;15%
            </div>
          )}
        </div>
      )}

      {/* Demo Widget Tab */}
      {activeTab === 'demo-widget' && (
        <AdminDemoWidget />
      )}

      {/* Blog Tab */}
      {activeTab === 'blog' && (
        <AdminBlog />
      )}

      {activeTab === 'broadcasts' && (
        <div className="container mx-auto px-3 md:px-4 py-6"><AdminBroadcasts /></div>
      )}

      {activeTab === 'green' && (
        <div className="container mx-auto px-3 md:px-4 py-6"><AdminGreenInstances /></div>
      )}

      <Footer />
    </div>
  );
}
